import type { CSSProperties } from 'react';
import type { FixedMenuCoords } from '@/components/ui/AnchoredDropdownMenu';

type GetProntuarioPanelAnchoredCoordsOptions = {
	menuWidth?: number;
	estimatedHeight?: number;
	gap?: number;
	padding?: number;
};

export const PRONTUARIO_PANEL_BOUNDARY_ATTR = 'data-prontuario-panel';

/** Posiciona menu fixo dentro do retângulo do painel do prontuário (não invade o vídeo). */
export const getProntuarioPanelAnchoredCoords = (
	anchor: HTMLElement,
	boundary: HTMLElement,
	{
		menuWidth = 280,
		estimatedHeight = 220,
		gap = 6,
		padding = 8,
	}: GetProntuarioPanelAnchoredCoordsOptions = {},
): FixedMenuCoords => {
	const anchorRect = anchor.getBoundingClientRect();
	const bounds = boundary.getBoundingClientRect();
	const width = Math.min(menuWidth, bounds.width - padding * 2);

	let left = anchorRect.left;
	left = Math.min(left, bounds.right - width - padding);
	left = Math.max(bounds.left + padding, left);

	const belowTop = anchorRect.bottom + gap;
	const belowSpace = bounds.bottom - padding - belowTop;
	const aboveAnchor = anchorRect.top - gap;
	const aboveSpace = aboveAnchor - (bounds.top + padding);

	const fitsBelow = belowSpace >= estimatedHeight;
	const fitsAbove = aboveSpace >= estimatedHeight;

	if (fitsBelow || (!fitsAbove && belowSpace >= aboveSpace)) {
		const maxTop = bounds.bottom - padding - estimatedHeight;
		return {
			top: Math.min(belowTop, maxTop),
			left,
			width,
		};
	}

	const minTop = bounds.top + padding + estimatedHeight;
	return {
		top: Math.max(aboveAnchor, minTop),
		left,
		width,
		transform: 'translateY(-100%)' as CSSProperties['transform'],
	};
};

export const findProntuarioPanelBoundary = (
	anchor: HTMLElement,
): HTMLElement | null =>
	anchor.closest<HTMLElement>(`[${PRONTUARIO_PANEL_BOUNDARY_ATTR}]`);
